import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import PageShell from '../components/PageShell'
import { useT } from '../i18n'
import {
  type ArtifactStatus,
  type ArtifactVisibility
} from '../utils/reportArtifactStorage'
import { listCollaborationArtifactSummaries, type CollaborationArtifactSummary } from '../utils/collaborationArtifacts'

type LibraryStatusFilter = 'all' | ArtifactStatus
type LibraryVisibilityFilter = 'all' | ArtifactVisibility

const isArtifactStatus = (value: string): value is ArtifactStatus => (
  value === 'draft' || value === 'published' || value === 'archived' || value === 'revoked'
)

const parseLibraryStatusFilter = (value: string | null): LibraryStatusFilter => {
  if (!value) return 'all'
  return isArtifactStatus(value) ? value : 'all'
}

const isArtifactVisibility = (value: string): value is ArtifactVisibility => (
  value === 'private' || value === 'group' || value === 'agora'
)

const parseLibraryVisibilityFilter = (value: string | null): LibraryVisibilityFilter => {
  if (!value) return 'all'
  return isArtifactVisibility(value) ? value : 'all'
}

const buildLibrarySearch = (
  searchQuery: string,
  statusFilter: LibraryStatusFilter,
  visibilityFilter: LibraryVisibilityFilter
): string => {
  const params = new URLSearchParams()
  const normalizedQuery = searchQuery.trim()

  if (normalizedQuery) {
    params.set('q', searchQuery)
  }

  if (statusFilter !== 'all') {
    params.set('status', statusFilter)
  }

  if (visibilityFilter !== 'all') {
    params.set('visibility', visibilityFilter)
  }

  return params.toString()
}

const getStatusBadgeStyle = (status: ArtifactStatus): React.CSSProperties => {
  if (status === 'published') {
    return { backgroundColor: '#dcfce7', color: '#166534' }
  }

  if (status === 'draft') {
    return { backgroundColor: '#dbeafe', color: '#1d4ed8' }
  }

  if (status === 'archived') {
    return { backgroundColor: '#f3f4f6', color: '#374151' }
  }

  return { backgroundColor: '#fee2e2', color: '#991b1b' }
}

const formatDateTime = (value: string | null): string => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString()
}

const getStatusLabel = (status: ArtifactStatus, t: ReturnType<typeof useT>): string => {
  switch (status) {
    case 'draft':
      return t('collaborationLibrary.status.draft')
    case 'published':
      return t('collaborationLibrary.status.published')
    case 'archived':
      return t('collaborationLibrary.status.archived')
    case 'revoked':
      return t('collaborationLibrary.status.revoked')
    default:
      return status
  }
}

const getVisibilityLabel = (visibility: ArtifactVisibility, t: ReturnType<typeof useT>): string => {
  switch (visibility) {
    case 'private':
      return t('collaborationLibrary.visibility.private')
    case 'group':
      return t('collaborationLibrary.visibility.group')
    case 'agora':
      return t('collaborationLibrary.visibility.agora')
    default:
      return visibility
  }
}

const getArtifactTypeLabel = (artifactType: CollaborationArtifactSummary['artifactType'], t: ReturnType<typeof useT>): string => {
  switch (artifactType) {
    case 'lot-story':
      return t('collaborationLibrary.type.lotStory')
    case 'report-snapshot':
    default:
      return t('collaborationLibrary.type.reportSnapshot')
  }
}

export default function CommunityArtifacts() {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const [summaries, setSummaries] = useState<CollaborationArtifactSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const loadSummaries = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)

    try {
      const nextSummaries = await listCollaborationArtifactSummaries()
      setSummaries(nextSummaries)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('collaborationLibrary.error.loadFailed')
      setLoadError(message)
      setSummaries([])
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadSummaries()
  }, [loadSummaries])

  const { searchQuery, statusFilter, visibilityFilter } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      searchQuery: params.get('q') ?? '',
      statusFilter: parseLibraryStatusFilter(params.get('status')),
      visibilityFilter: parseLibraryVisibilityFilter(params.get('visibility'))
    }
  }, [location.search])

  const filteredSummaries = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    return summaries.filter((summary) => {
      if (statusFilter !== 'all' && summary.status !== statusFilter) {
        return false
      }

      if (visibilityFilter !== 'all' && summary.visibility !== visibilityFilter) {
        return false
      }

      if (!normalizedQuery) return true

      const haystacks = [
        summary.title,
        summary.contextLine,
        summary.summary,
        summary.sourceLabel,
        ...summary.highlights
      ]
      return haystacks.some((value) => value.toLowerCase().includes(normalizedQuery))
    })
  }, [searchQuery, statusFilter, summaries, visibilityFilter])

  const reloadSummaries = () => {
    void loadSummaries()
  }

  const updateLibraryFilters = ({
    nextSearchQuery = searchQuery,
    nextStatusFilter = statusFilter,
    nextVisibilityFilter = visibilityFilter
  }: {
    nextSearchQuery?: string
    nextStatusFilter?: LibraryStatusFilter
    nextVisibilityFilter?: LibraryVisibilityFilter
  }) => {
    const nextSearch = buildLibrarySearch(nextSearchQuery, nextStatusFilter, nextVisibilityFilter)
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search

    if (nextSearch === currentSearch) {
      return
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    )
  }

  let content: React.ReactNode

  if (isLoading && summaries.length === 0) {
    content = (
      <div className="form-card" style={{ marginTop: 16 }}>
        <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationLibrary.loading')}</h3>
      </div>
    )
  } else if (summaries.length === 0) {
    content = (
      <div className="form-card" style={{ marginTop: 16 }}>
        <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationLibrary.empty.title')}</h3>
        <p className="lead">{t('collaborationLibrary.empty.subtitle')}</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          <Link to="/reports" className="btn-gradient" style={{ textDecoration: 'none' }}>
            {t('collaborationLibrary.empty.openReports')}
          </Link>
          <Link to="/lots" style={{ alignSelf: 'center' }}>
            {t('collaborationLibrary.empty.openLots')}
          </Link>
        </div>
      </div>
    )
  } else if (filteredSummaries.length === 0) {
    content = (
      <div className="form-card" style={{ marginTop: 16 }}>
        <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationLibrary.empty.noResultsTitle')}</h3>
        <p className="lead">{t('collaborationLibrary.empty.noResultsSubtitle')}</p>
      </div>
    )
  } else {
    content = (
      <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
        {filteredSummaries.map((summary) => {
          const publishedAt = formatDateTime(summary.publishedAt)
          const updatedAt = formatDateTime(summary.updatedAt)
          const artifactDetailHref = `/community/artifacts/${encodeURIComponent(summary.artifactId)}${location.search}`

          return (
            <div
              key={summary.artifactId}
              className="form-card"
              data-testid={`collaboration-library-artifact-${summary.artifactId}`}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  alignItems: 'start'
                }}
              >
                <div style={{ minWidth: 0, flex: '1 1 420px' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span
                      style={{
                        padding: '4px 9px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                        backgroundColor: '#eef2ff',
                        color: '#3730a3'
                      }}
                    >
                      {getArtifactTypeLabel(summary.artifactType, t)}
                    </span>
                    <span
                      style={{
                        padding: '4px 9px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                        ...getStatusBadgeStyle(summary.status)
                      }}
                    >
                      {getStatusLabel(summary.status, t)}
                    </span>
                  </div>

                  <h3 className="h2" style={{ marginTop: 0, marginBottom: 6 }}>{summary.title}</h3>
                  <div className="lead">{summary.contextLine}</div>
                  <p style={{ marginTop: 12, marginBottom: 0, color: '#374151' }}>{summary.summary}</p>
                </div>

                <div style={{ minWidth: 220, display: 'grid', gap: 6 }}>
                  <div><b>{t('collaborationLibrary.meta.version')}</b> {summary.currentVersion}</div>
                  <div><b>{t('collaborationLibrary.meta.visibility')}</b> {getVisibilityLabel(summary.visibility, t)}</div>
                  <div><b>{t('collaborationLibrary.meta.ownerTenant')}</b> {summary.ownerTenantKey}</div>
                  <div><b>{t('collaborationLibrary.meta.publishedAt')}</b> {publishedAt || t('collaborationLibrary.meta.notPublishedYet')}</div>
                  <div><b>{t('collaborationLibrary.meta.updatedAt')}</b> {updatedAt || t('collaborationLibrary.meta.unknown')}</div>
                </div>
              </div>

              {summary.highlights.length > 0 ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  {summary.highlights.map((highlight) => (
                    <span
                      key={highlight}
                      style={{
                        padding: '4px 9px',
                        borderRadius: 999,
                        fontSize: 12,
                        backgroundColor: '#f3f4f6',
                        color: '#374151'
                      }}
                    >
                      {highlight}
                    </span>
                  ))}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16, alignItems: 'center' }}>
                <Link to={artifactDetailHref} className="btn-gradient" style={{ textDecoration: 'none' }}>
                  {t('collaborationLibrary.action.openDetail')}
                </Link>
                <Link to={summary.sourceHref} className="btn-gradient" style={{ textDecoration: 'none' }}>
                  {t('collaborationLibrary.action.openSource')}
                </Link>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <PageShell>
      <h2 className="h2">{t('collaborationLibrary.title')}</h2>
      <p className="lead">{t('collaborationLibrary.subtitle')}</p>

      <div className="form-card" style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'end',
            flexWrap: 'wrap'
          }}
        >
          <label style={{ display: 'grid', gap: 6, flex: '1 1 320px' }}>
            <span style={{ fontWeight: 700 }}>{t('collaborationLibrary.searchLabel')}</span>
            <input
              value={searchQuery}
              onChange={(event) => updateLibraryFilters({ nextSearchQuery: event.target.value })}
              placeholder={t('collaborationLibrary.searchPlaceholder')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6, minWidth: 220 }}>
            <span style={{ fontWeight: 700 }}>{t('collaborationLibrary.statusFilterLabel')}</span>
            <select
              value={statusFilter}
              onChange={(event) => updateLibraryFilters({ nextStatusFilter: parseLibraryStatusFilter(event.target.value) })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
            >
              <option value="all">{t('collaborationLibrary.statusFilter.all')}</option>
              <option value="published">{t('collaborationLibrary.status.published')}</option>
              <option value="revoked">{t('collaborationLibrary.status.revoked')}</option>
              <option value="archived">{t('collaborationLibrary.status.archived')}</option>
              <option value="draft">{t('collaborationLibrary.status.draft')}</option>
            </select>
          </label>

          <label style={{ display: 'grid', gap: 6, minWidth: 220 }}>
            <span style={{ fontWeight: 700 }}>{t('collaborationLibrary.visibilityFilterLabel')}</span>
            <select
              value={visibilityFilter}
              onChange={(event) => updateLibraryFilters({ nextVisibilityFilter: parseLibraryVisibilityFilter(event.target.value) })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
            >
              <option value="all">{t('collaborationLibrary.visibilityFilter.all')}</option>
              <option value="private">{t('collaborationLibrary.visibility.private')}</option>
              <option value="group">{t('collaborationLibrary.visibility.group')}</option>
              <option value="agora">{t('collaborationLibrary.visibility.agora')}</option>
            </select>
          </label>

          <button type="button" onClick={reloadSummaries} disabled={isLoading}>
            {t('collaborationLibrary.refresh')}
          </button>
        </div>

        <div className="lead" style={{ marginTop: 12 }}>
          {t('collaborationLibrary.count', {
            visible: String(filteredSummaries.length),
            total: String(summaries.length)
          })}
        </div>
      </div>

      {loadError ? (
        <div className="form-card" style={{ marginTop: 16, color: '#b91c1c' }}>
          {loadError}
        </div>
      ) : null}

      {content}
    </PageShell>
  )
}